<template>
  <div class="users-panel">
    <!-- Tab bar -->
    <div class="users-tabs">
      <button
        type="button"
        class="utab"
        :class="{ active: activeTab === 'gestao' }"
        @click="activeTab = 'gestao'"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        Gestão
      </button>
      <button
        type="button"
        class="utab"
        :class="{ active: activeTab === 'lista' }"
        @click="activeTab = 'lista'"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
          <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
        </svg>
        Lista de usuários
      </button>
      <button
        type="button"
        class="utab"
        :class="{ active: activeTab === 'cargos' }"
        @click="activeTab = 'cargos'"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
        </svg>
        Lista de cargos
      </button>
      <button
        type="button"
        class="utab"
        :class="{ active: activeTab === 'offline' }"
        @click="activeTab = 'offline'"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8h4M21 6v4"/>
        </svg>
        Cadastro de Funcionarios Nao Logados
      </button>
    </div>

    <!-- Tab content -->
    <div class="users-tab-content">
      <UsersGestaoTab
        v-if="activeTab === 'gestao'"
        :accounts="accounts"
        :roles="roles"
      />

      <UsersListTab
        v-else-if="activeTab === 'lista'"
        :accounts="accounts"
        :current-uid="currentUid"
        :current-user-role="currentUserRole"
        :show-account-form="showAccountForm"
        :account-form="accountForm"
        :account-error="accountError"
        :roles="roles"
        :account-subordinate-options="accountSubordinateOptions"
        :is-analyst="isAnalyst"
        @refresh-accounts="$emit('refresh-accounts')"
        @open-account-form="$emit('open-account-form')"
        @edit-account="$emit('edit-account', $event)"
        @delete-account="$emit('delete-account', $event)"
        @save-account="$emit('save-account')"
        @close-account-form="$emit('close-account-form')"
        @update-account-form="$emit('update-account-form', $event)"
        @add-account-subordinate="$emit('add-account-subordinate')"
        @remove-account-subordinate="$emit('remove-account-subordinate', $event)"
      />

      <UsersRolesTab
        v-else-if="activeTab === 'cargos'"
        :roles="roles"
        :show-role-form="showRoleForm"
        :role-form="roleForm"
        :role-error="roleError"
        :is-analyst="isAnalyst"
        @refresh-roles="$emit('refresh-roles')"
        @open-role-form="$emit('open-role-form')"
        @edit-role="$emit('edit-role', $event)"
        @save-role="$emit('save-role')"
        @close-role-form="$emit('close-role-form')"
        @update-role-form="$emit('update-role-form', $event)"
        @add-role-subordinate="$emit('add-role-subordinate')"
        @remove-role-subordinate="$emit('remove-role-subordinate', $event)"
      />

      <UsersOfflineEmployeesTab
        v-else-if="activeTab === 'offline'"
        :employees="offlineEmployees"
        :roles="roles"
        :show-form="showOfflineEmployeeForm"
        :form="offlineEmployeeForm"
        :error="offlineEmployeeError"
        :is-analyst="isAnalyst"
        @open-form="$emit('open-offline-employee-form')"
        @close-form="$emit('close-offline-employee-form')"
        @save-employee="$emit('save-offline-employee')"
        @edit-employee="$emit('edit-offline-employee', $event)"
        @delete-employee="$emit('delete-offline-employee', $event)"
        @update-form="$emit('update-offline-employee-form', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType } from 'vue'
import UsersGestaoTab from './users/UsersGestaoTab.vue'
import UsersListTab from './users/UsersListTab.vue'
import UsersRolesTab from './users/UsersRolesTab.vue'
import UsersOfflineEmployeesTab from './users/UsersOfflineEmployeesTab.vue'
import type { Account } from '@/store/accounts'
import type { Role } from '@/store/roles'
import type { OfflineEmployee } from '@/store/offlineEmployees'

interface AccountFormModel {
  uid: string
  name: string
  username: string
  password: string
  email: string
  birthDate: string
  role: 'admin-master' | 'admin-obra' | 'user'
  roleId: string
  subordinateUsernames: string[]
  isEditing: boolean
}

interface RoleFormModel {
  id: string
  name: string
  isAdmin: boolean
  isAnalyst: boolean
  subordinateRoleIds: string[]
  warehouseAccess: number
  maintenanceAccess: number
  constructionAccess: number
  isEditing: boolean
}

interface OfflineEmployeeFormModel {
  id: string
  name: string
  username: string
  roleId: string
  photoUrl: string
  isEditing: boolean
}

export default defineComponent({
  name: 'UsersPanel',
  components: { UsersGestaoTab, UsersListTab, UsersRolesTab, UsersOfflineEmployeesTab },
  props: {
    accounts: { type: Array as PropType<Account[]>, default: () => [] },
    currentUid: { type: String, default: '' },
    showAccountForm: { type: Boolean, default: false },
    accountForm: { type: Object as PropType<AccountFormModel>, required: true },
    accountError: { type: String, default: '' },
    roles: { type: Array as PropType<Role[]>, default: () => [] },
    currentUserRole: { type: String, default: 'user' },
    showRoleForm: { type: Boolean, default: false },
    roleForm: { type: Object as PropType<RoleFormModel>, required: true },
    roleError: { type: String, default: '' },
    offlineEmployees: { type: Array as PropType<OfflineEmployee[]>, default: () => [] },
    showOfflineEmployeeForm: { type: Boolean, default: false },
    offlineEmployeeForm: { type: Object as PropType<OfflineEmployeeFormModel>, required: true },
    offlineEmployeeError: { type: String, default: '' },
    accountSubordinateOptions: { type: Array as PropType<Array<{ username: string; name: string }>>, default: () => [] },
    isAnalyst: { type: Boolean, default: false }
  },
  emits: [
    'refresh-accounts', 'open-account-form', 'edit-account', 'delete-account',
    'save-account', 'close-account-form', 'update-account-form',
    'add-account-subordinate', 'remove-account-subordinate',
    'refresh-roles', 'open-role-form', 'edit-role', 'save-role', 'close-role-form',
    'add-role-subordinate', 'remove-role-subordinate', 'update-role-form',
    'open-offline-employee-form', 'close-offline-employee-form', 'save-offline-employee',
    'edit-offline-employee', 'delete-offline-employee', 'update-offline-employee-form'
  ],
  setup() {
    const activeTab = ref<'gestao' | 'lista' | 'cargos' | 'offline'>('gestao')
    return { activeTab }
  }
})
</script>

<style scoped lang="scss">
.users-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.users-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 2px solid rgba(31,30,26,.1);
  padding-bottom: 0;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.utab {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  background: transparent;
  border: none;
  border-bottom: 2.5px solid transparent;
  margin-bottom: -2px;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-muted, #888);
  cursor: pointer;
  transition: color .15s, border-color .15s;
  border-radius: 0;

  &:hover { color: var(--ink, #2c2b27); }
  &.active {
    color: var(--accent, #2f6fce);
    border-bottom-color: var(--accent, #2f6fce);
  }

  svg { flex-shrink: 0; }
}

.users-tab-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
</style>
